import dayjs from "dayjs";
import { KYDEventBlockData, KYDEventBlockDataV2 } from "interfaces/KYDCampaign";
import { KYDGender } from "interfaces/KYDUser";
import {
  CampaignType,
  MarketingAd,
  OptimizationGoal,
  RatioType,
  PublisherPlatforms,
  RatioPositions,
  FacebookPositions,
  InstagramPositions,
} from "../../interfaces/KYDMarketing";

export const genderToText = (gender: KYDGender | "any") => {
  switch (gender) {
    case "male":
      return "Male";
    case "female":
      return "Female";
    case "any":
      return "Any";
    case "prefernottosay":
      return "-";
  }
};

export const replaceCampaignVariables = (
  text: string,
  eventData: KYDEventBlockData | KYDEventBlockDataV2
) => {
  // Safety check for required data
  if (!text || !eventData?.event) {
    return text || "";
  }
  const availableVariables = [
    {
      dynamicKey: "{{start_at}}",
      objectKey: "start_at",
      format: (value: string) => dayjs(value).format("MMMM D, YYYY"),
    },
    {
      dynamicKey: "{{event_name}}",
      objectKey: "name",
    },
    {
      dynamicKey: "{{end_at}}",
      objectKey: "end_at",
      format: (value: string) => dayjs(value).format("MMMM D, YYYY"),
    },
    {
      dynamicKey: "{{link}}",
      objectKey: "link",
    },
    {
      dynamicKey: "{{venue}}",
      objectKey: "venue",
    },
  ];

  // Helper function to get variable value
  const getVariableValue = (variableKey: string) => {
    // Try to find variable with exact match first
    let variable = availableVariables.find((v) => v.dynamicKey === variableKey);

    // If not found, try with normalized underscores (for escaped variables)
    if (!variable) {
      const normalizedKey = variableKey.replace(/\\_/g, "_");
      variable = availableVariables.find((v) => v.dynamicKey === normalizedKey);
    }

    if (!variable) return null;

    // Get the value from the event object
    let value: any;
    if (variable.objectKey.includes(".")) {
      value = variable.objectKey
        .split(".")
        .reduce((obj, key) => obj?.[key], eventData.event);
    } else {
      value =
        eventData.event[variable.objectKey as keyof typeof eventData.event];
    }

    // Apply formatting if available
    if (variable.format && value) {
      return variable.format(value as string);
    }

    return value || "";
  };

  if (typeof text !== "string") {
    return text;
  }

  return text?.replace(/{{([^}]+)}}/g, (match: string, varName: string) => {
    // varName is the content between {{ and }}, so we need to reconstruct the full key
    const fullKey = `{{${varName}}}`;
    const value = getVariableValue(fullKey);
    return value || match;
  });
};

export const getDefaultOptimizationGoalForCampaignType = (
  campaign_type: CampaignType
): OptimizationGoal => {
  const default_goal_for_optimization_type: Record<
    CampaignType,
    OptimizationGoal
  > = {
    [CampaignType.AWARENESS]: OptimizationGoal.LANDING_PAGE_VIEWS,
    [CampaignType.CONVERSION]: OptimizationGoal.VALUE,
  };

  return default_goal_for_optimization_type[campaign_type];
};

export const resolveOptimizationGoalForCampaignType = ({
  campaign_type,
  configured_optimization_goal,
}: {
  campaign_type: CampaignType;
  configured_optimization_goal?: string | null;
}): OptimizationGoal => {
  const defaultGoal = getDefaultOptimizationGoalForCampaignType(campaign_type);
  if (!configured_optimization_goal) return defaultGoal;

  const supportedGoals = new Set(Object.values(OptimizationGoal));
  if (supportedGoals.has(configured_optimization_goal as OptimizationGoal)) {
    return configured_optimization_goal as OptimizationGoal;
  }

  return defaultGoal;
};

/**
 * Gets default positions for Facebook and Instagram based on media ratios
 * @param ratios Array of media ratios (portrait, square, etc.)
 * @returns Object containing default positions for each platform
 */
export const getDefaultPositionsFromRatios = (ratios: RatioType[]) => {
  const facebookPositions: FacebookPositions[] = [];
  const instagramPositions: InstagramPositions[] = [];

  // Get unique ratios
  const uniqueRatios = [...new Set(ratios)];

  uniqueRatios.forEach((ratio) => {
    if (RatioPositions[ratio]) {
      if (RatioPositions[ratio][PublisherPlatforms.facebook]) {
        facebookPositions.push(
          ...RatioPositions[ratio][PublisherPlatforms.facebook]
        );
      }
      if (RatioPositions[ratio][PublisherPlatforms.instagram]) {
        instagramPositions.push(
          ...RatioPositions[ratio][PublisherPlatforms.instagram]
        );
      }
    }
  });

  return {
    facebook: [...new Set(facebookPositions)], // Remove duplicates
    instagram: [...new Set(instagramPositions)], // Remove duplicates
  };
};

const unique = <T>(items: T[]) => [...new Set(items)];

const instagramExplorePlacementsRequiringFeed: InstagramPositions[] = [
  InstagramPositions.explore,
  InstagramPositions.explore_home,
];

/**
 * Meta rejects `feed` in `instagram_positions` (ad set targeting and asset customization).
 * Instagram feed is `stream`. Maps legacy stored `feed` to `stream` and dedupes.
 * Explore placements require stream as well (Graph API: “Instagram Explore … must have … feed”).
 */
export function normalizeInstagramPositionsForMetaApi(
  positions: InstagramPositions[]
): InstagramPositions[] {
  const mapped = unique(
    positions.map((p) =>
      p === InstagramPositions.feed ? InstagramPositions.stream : p
    )
  );
  const hasExplore = mapped.some((p) =>
    instagramExplorePlacementsRequiringFeed.includes(p)
  );
  if (hasExplore && !mapped.includes(InstagramPositions.stream)) {
    return unique([...mapped, InstagramPositions.stream]);
  }
  return mapped;
}

/**
 * Default Facebook placements when a creative has a page but no stored
 * `facebook_positions` / `customization_rules` yet (e.g. persisted creative without
 * rules, or Facebook-only ad → user adds Instagram while rules were never stored).
 * Kept in sync with `DEFAULT_CREATIVE_TEMPLATE` in `launchCampaignService`.
 */
export const DEFAULT_FACEBOOK_PLACEMENTS_FOR_META_CREATIVE: FacebookPositions[] =
  [
    FacebookPositions.feed,
    FacebookPositions.story,
    FacebookPositions.facebook_reels,
  ];

/**
 * Default Instagram placements when a creative enables an IG actor but has no stored
 * `instagram_positions` yet (e.g. Facebook-only ad → user adds Instagram). Kept in
 * sync with the managed campaign launch creative template in `launchCampaignService`.
 * `stream` is Instagram feed; Meta requires it when using Explore / explore_home.
 */
export const DEFAULT_INSTAGRAM_PLACEMENTS_FOR_META_CREATIVE: InstagramPositions[] =
  [
    InstagramPositions.stream,
    InstagramPositions.explore_home,
    InstagramPositions.profile_feed,
    InstagramPositions.explore,
    InstagramPositions.story,
    InstagramPositions.reels,
  ];

export type MetaCreativeIdentityForPlacements = {
  [PublisherPlatforms.facebook]?: { page_id?: string };
  [PublisherPlatforms.instagram]?: { instagram_actor_id?: string };
};

/**
 * When a creative uses both a Facebook page and an Instagram actor, placements should match
 * managed launch (`DEFAULT_*_PLACEMENTS_FOR_META_CREATIVE`), not partial legacy rules from
 * Facebook-only creation. Used by guided-edit serializers and ad set placement resolution.
 */
export function getCanonicalFacebookInstagramPlacementsForDualIdentity(
  identity: MetaCreativeIdentityForPlacements | undefined
): {
  facebook: FacebookPositions[];
  instagram: InstagramPositions[];
} | null {
  const hasPage = Boolean(
    identity?.[PublisherPlatforms.facebook]?.page_id?.trim()
  );
  const hasIg = Boolean(
    identity?.[PublisherPlatforms.instagram]?.instagram_actor_id?.trim()
  );
  if (!hasPage || !hasIg) {
    return null;
  }
  return {
    facebook: [...DEFAULT_FACEBOOK_PLACEMENTS_FOR_META_CREATIVE],
    instagram: [...DEFAULT_INSTAGRAM_PLACEMENTS_FOR_META_CREATIVE],
  };
}

/** Union of `instagram_positions` from asset customization rules on ads, normalized for Meta API. */
export function collectNormalizedInstagramPositionsFromAds(
  ads: MarketingAd[] | undefined
): InstagramPositions[] {
  const raw =
    ads?.flatMap(
      (a) =>
        a.creative?.customization_rules?.flatMap((c) => {
          const { customization } = c;
          if (!("instagram_positions" in customization)) return [];
          return customization.instagram_positions ?? [];
        }) ?? []
    ) ?? [];
  return normalizeInstagramPositionsForMetaApi(raw);
}

const filterPositionsByAllowed = <T>(positions: T[], allowed: T[]) => {
  const allowedSet = new Set(allowed);
  return unique(positions.filter((position) => allowedSet.has(position)));
};

export type GetAssetFeedPlacementConfigInput = {
  facebookPositionsProvided: boolean;
  instagramPositionsProvided: boolean;
  facebookPositions: FacebookPositions[];
  instagramPositions: InstagramPositions[];
  availableRatios?: RatioType[];
};

export const getAssetFeedPlacementConfig = ({
  facebookPositionsProvided,
  instagramPositionsProvided,
  facebookPositions,
  instagramPositions,
  availableRatios,
}: GetAssetFeedPlacementConfigInput) => {
  const resolvedRatios =
    availableRatios && availableRatios.length > 0
      ? unique(availableRatios)
      : [RatioType.square, RatioType.portrait];
  const hasPortraitRatio = resolvedRatios.includes(RatioType.portrait);
  const hasSquareRatio = resolvedRatios.includes(RatioType.square);

  const defaultFacebookPortraitPositions = hasPortraitRatio
    ? RatioPositions[RatioType.portrait][PublisherPlatforms.facebook]
    : [];
  const defaultInstagramPortraitPositions = hasPortraitRatio
    ? RatioPositions[RatioType.portrait][PublisherPlatforms.instagram]
    : [];
  const defaultFacebookSquarePositions = hasSquareRatio
    ? RatioPositions[RatioType.square][PublisherPlatforms.facebook]
    : [];
  const defaultInstagramSquarePositions = hasSquareRatio
    ? RatioPositions[RatioType.square][PublisherPlatforms.instagram]
    : [];

  const facebookPositionsForPortrait =
    facebookPositionsProvided && facebookPositions.length > 0
      ? filterPositionsByAllowed(
          facebookPositions,
          defaultFacebookPortraitPositions
        )
      : [];
  const facebookPositionsForSquare =
    facebookPositionsProvided && facebookPositions.length > 0
      ? filterPositionsByAllowed(
          facebookPositions,
          defaultFacebookSquarePositions
        )
      : [];
  const instagramPositionsForPortrait =
    instagramPositionsProvided && instagramPositions.length > 0
      ? filterPositionsByAllowed(
          instagramPositions,
          defaultInstagramPortraitPositions
        )
      : [];
  const instagramPositionsForSquare =
    instagramPositionsProvided && instagramPositions.length > 0
      ? filterPositionsByAllowed(
          instagramPositions,
          defaultInstagramSquarePositions
        )
      : [];

  const resolvedPublisherPlatforms: PublisherPlatforms[] = [];
  const hasValidFacebookPositions =
    facebookPositionsForPortrait.length > 0 ||
    facebookPositionsForSquare.length > 0;
  const hasValidInstagramPositions =
    instagramPositionsForPortrait.length > 0 ||
    instagramPositionsForSquare.length > 0;

  /**
   * Only target Instagram when we have explicit Instagram placement rules. If we omit IG rules
   * (Facebook-only creative / no `instagram_actor_id`), we must not add `instagram` to
   * `publisher_platforms` or default IG positions — Meta rejects with a prompt to select an
   * Instagram account for the business.
   */
  const includeInstagramPlatform = instagramPositionsProvided
    ? hasValidInstagramPositions
    : false;

  if (facebookPositionsProvided ? hasValidFacebookPositions : true) {
    resolvedPublisherPlatforms.push(PublisherPlatforms.facebook);
  }
  if (includeInstagramPlatform) {
    resolvedPublisherPlatforms.push(PublisherPlatforms.instagram);
  }

  // `normIg` may append `stream` when Explore placements are present (Meta API requirement).
  const exploreInstagramRaw = !includeInstagramPlatform
    ? []
    : instagramPositionsProvided
      ? instagramPositionsForSquare
      : defaultInstagramSquarePositions;
  const singleAssetInstagramRaw = !includeInstagramPlatform
    ? []
    : instagramPositionsProvided
      ? unique([
          ...instagramPositionsForPortrait,
          ...instagramPositionsForSquare,
        ])
      : unique([
          ...defaultInstagramSquarePositions,
          ...defaultInstagramPortraitPositions,
        ]);

  const normIg = (list: InstagramPositions[]) =>
    normalizeInstagramPositionsForMetaApi(unique(list));

  return {
    publisherPlatforms: resolvedPublisherPlatforms,
    reels: {
      facebook: facebookPositionsProvided
        ? facebookPositionsForPortrait
        : defaultFacebookPortraitPositions,
      instagram: normIg(
        !includeInstagramPlatform
          ? []
          : instagramPositionsProvided
            ? instagramPositionsForPortrait
            : defaultInstagramPortraitPositions
      ),
    },
    explore: {
      facebook: facebookPositionsProvided
        ? facebookPositionsForSquare
        : defaultFacebookSquarePositions,
      instagram: normIg(exploreInstagramRaw),
    },
    singleAsset: {
      facebook: facebookPositionsProvided
        ? unique([
            ...facebookPositionsForPortrait,
            ...facebookPositionsForSquare,
          ])
        : unique([
            ...defaultFacebookSquarePositions,
            ...defaultFacebookPortraitPositions,
          ]),
      instagram: normIg(singleAssetInstagramRaw),
    },
  };
};

export type AssetFeedPlacementConfig = ReturnType<
  typeof getAssetFeedPlacementConfig
>;

/**
 * Placement-optimized asset feeds need two customization rules with distinct placement targeting
 * (reels/portrait bucket vs explore/feed bucket). When the ad only lists square placements (e.g. feed)
 * or only portrait placements (e.g. story), the other bucket is empty — Meta rejects with 2446428.
 * Fill the empty bucket from Meta’s standard portrait vs square placement groups so both rules are
 * real targets; the ad set still restricts delivery. Used when building `asset_customization_rules`.
 */
export const augmentPlacementConfigForAssetFeedDualRules = (
  config: AssetFeedPlacementConfig
): AssetFeedPlacementConfig => {
  const normIg = (list: InstagramPositions[]) =>
    normalizeInstagramPositionsForMetaApi(unique(list));

  const metaFacebookPortrait =
    RatioPositions[RatioType.portrait][PublisherPlatforms.facebook];
  const metaFacebookSquare =
    RatioPositions[RatioType.square][PublisherPlatforms.facebook];
  const metaInstagramPortrait =
    RatioPositions[RatioType.portrait][PublisherPlatforms.instagram];
  const metaInstagramSquare =
    RatioPositions[RatioType.square][PublisherPlatforms.instagram];

  let reelsFacebook = [...config.reels.facebook];
  let exploreFacebook = [...config.explore.facebook];
  let reelsInstagram = [...config.reels.instagram];
  let exploreInstagram = [...config.explore.instagram];

  if (reelsFacebook.length === 0 && exploreFacebook.length > 0) {
    reelsFacebook = [...metaFacebookPortrait];
  }
  if (exploreFacebook.length === 0 && reelsFacebook.length > 0) {
    exploreFacebook = [...metaFacebookSquare];
  }

  if (reelsInstagram.length === 0 && exploreInstagram.length > 0) {
    reelsInstagram = normIg([...metaInstagramPortrait]);
  }
  if (exploreInstagram.length === 0 && reelsInstagram.length > 0) {
    exploreInstagram = normIg([...metaInstagramSquare]);
  }

  return {
    ...config,
    reels: {
      facebook: reelsFacebook,
      instagram: reelsInstagram,
    },
    explore: {
      facebook: exploreFacebook,
      instagram: exploreInstagram,
    },
    singleAsset: {
      facebook: unique([...reelsFacebook, ...exploreFacebook]),
      instagram: normIg(unique([...reelsInstagram, ...exploreInstagram])),
    },
  };
};
